import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Solutions.css';

const solutions = [
  {
    id: 'monitoring',
    icon: '⬡',
    title: 'PC Health Monitoring',
    tagline: 'See everything. Fix it before it breaks.',
    overview:
      'Bluepeak deploys lightweight agents on every endpoint in your organization, streaming health data to a centralized dashboard. Disk health, CPU load, RAM usage, patch status, and security posture — all visible in real time.',
    features: [
      'Real-time CPU, RAM, disk, and temperature tracking',
      'Automated patch compliance reporting',
      'Predictive failure alerts before hardware degrades',
      'Software inventory and license tracking',
      'Monthly health reports for management',
    ],
    who: 'Accounting firms with 10–200 endpoints. Law firms requiring audit-ready IT documentation.',
  },
  {
    id: 'workstation',
    icon: '◧',
    title: 'Workstation Setup & Deployment',
    tagline: 'New hire on Monday. Machine ready Friday.',
    overview:
      'We handle the full lifecycle of a workstation — procurement guidance, OS imaging, software configuration, domain joining, security hardening, and documentation. Every machine is handed off tested, documented, and ready.',
    features: [
      'Custom OS imaging for your environment',
      'Active Directory / Entra ID integration',
      'Security baseline hardening (CIS / NIST)',
      'Remote and on-site deployment options',
      'Full asset documentation and tagging',
    ],
    who: 'Any firm onboarding staff regularly. School boards rolling out device fleets.',
  },
  {
    id: 'remote',
    icon: '↗',
    title: 'Remote Support',
    tagline: 'Help arrives in minutes, not days.',
    overview:
      'When something breaks, your team needs help immediately. Bluepeak provides fast, secure remote support via encrypted sessions. We resolve the issue and document what happened — so it doesn\'t happen again.',
    features: [
      'Sub-30-minute response time for active incidents',
      'Encrypted remote desktop sessions',
      'Helpdesk ticketing and issue tracking',
      'Escalation paths for on-site visits',
      'Monthly incident reports and root cause analysis',
    ],
    who: 'Distributed teams, remote work environments, any business that can\'t wait for on-site.',
  },
  {
    id: 'cybersecurity',
    icon: '⬛',
    title: 'Cybersecurity Solutions',
    tagline: 'Protect client data. Meet compliance. Sleep better.',
    overview:
      'Cybersecurity for professional services isn\'t optional — it\'s a regulatory and fiduciary responsibility. Bluepeak assesses your current posture, closes gaps, and implements layered defenses calibrated for your risk profile.',
    features: [
      'Endpoint Detection & Response (EDR) deployment',
      'Email security and anti-phishing controls',
      'Multi-factor authentication rollout',
      'Dark web credential monitoring',
      'Annual security risk assessments',
      'Staff phishing simulation and training',
      'Incident response planning',
    ],
    who: 'Law firms handling client privilege. Accounting firms managing financial records. Any regulated business.',
  },
  {
    id: 'consulting',
    icon: '◉',
    title: 'IT Consulting',
    tagline: 'Strategy before spending.',
    overview:
      'Before you buy a new system, sign a cloud contract, or hire in-house IT, talk to us. Bluepeak provides vendor-neutral technology strategy — helping you understand your options and avoid expensive mistakes.',
    features: [
      'Technology roadmap development',
      'Cloud migration planning (Microsoft 365, Azure)',
      'Vendor evaluation and procurement support',
      'IT budget planning and cost optimization',
      'vCIO services for growing firms',
    ],
    who: 'Firms making significant IT decisions. Practices preparing for growth or an audit.',
  },
  {
    id: 'compliance',
    icon: '⬜',
    title: 'Compliance & Policy',
    tagline: 'Audit-ready infrastructure, documented.',
    overview:
      'Regulators and insurers are asking harder questions about IT. Bluepeak builds the documentation, policies, and technical controls that prove your environment is managed responsibly.',
    features: [
      'Acceptable Use and IT Security policies',
      'Data retention and destruction procedures',
      'Cyber insurance questionnaire support',
      'PIPEDA / privacy compliance guidance',
      'Disaster recovery and business continuity planning',
    ],
    who: 'Law Societies, CPA firms, any organization needing documented IT governance.',
  },
];

export default function Solutions() {
  const [active, setActive] = useState(solutions[0].id);
  const current = solutions.find(s => s.id === active);

  return (
    <div className="solutions-page">
      <div className="page-hero">
        <div className="container">
          <span className="tag">Solutions</span>
          <h1>What Bluepeak delivers.</h1>
          <p>
            Six practice areas. One team. Every service designed for organizations
            where IT failure isn't an acceptable outcome.
          </p>
        </div>
      </div>

      <div className="container sol-layout">
        <nav className="sol-nav">
          {solutions.map(s => (
            <button
              key={s.id}
              className={`sol-nav-item ${active === s.id ? 'active' : ''}`}
              onClick={() => setActive(s.id)}
            >
              <span className="sol-nav-icon">{s.icon}</span>
              <span>{s.title}</span>
            </button>
          ))}
        </nav>

        <div className="sol-detail">
          <span className="sol-icon">{current.icon}</span>
          <h2>{current.title}</h2>
          <p className="sol-tagline">{current.tagline}</p>
          <p className="sol-overview">{current.overview}</p>

          <div className="sol-features">
            <h4>What's included</h4>
            <ul>
              {current.features.map(f => (
                <li key={f}>
                  <span className="check">✓</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="sol-who">
            <span className="tag">Best fit</span>
            <p>{current.who}</p>
          </div>

          <Link to="/contact" className="btn-primary">
            Ask about {current.title} →
          </Link>
        </div>
      </div>
    </div>
  );
}
